import { theme } from 'antd';
import dayjs, { tz } from 'dayjs';
import { currentAcceptableDate, getCurrentTime, isDateInFuture } from 'library/helpers/bookingHelpers';
import SuperFetch from 'library/helpers/superFetch';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useGetPersonDisplayName } from 'library/hooks';
import _ from 'lodash';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bookingActions } from 'reduxStore/bookings/slice';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, TimePicker, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Text } = Typography;
const { TextArea } = Input;

const INITIAL_HOURS_DATA = {
	startHour: dayjs().format('HH:mm'),
	endHour: dayjs().add(1, 'hour').format('HH:mm')
};

const INITIAL_FORM_DATA = {
	startTime: dayjs(new Date(), 'YYYY-MM-DD'),
	endTime: dayjs(new Date(), 'YYYY-MM-DD'),
	startHour: dayjs(getCurrentTime(), 'HH:mm'),
	endHour: dayjs(getCurrentTime(), 'HH:mm').add(1, 'hour'),
	description: null,
	name: '',
	deviceID: null
};

const INITIAL_ERROR_DATA = {
	error: false,
	message: '',
	type: ''
};

const AddBookingModal = ({ isEdit = false, dataToUpdate = {}, children }) => {
	const {
		token: { colorTextDescription }
	} = theme.useToken();
	const { data: peopleData } = useSelector((state) => state.People);
	const { data: devicesData } = useSelector((state) => state.Devices);
	const { FbPersonID } = useSelector((state) => state.Auth.selectedProfile);
	const {
		remoteConfig: { constants, bookingType }
	} = useSelector((state) => state.FbRemoteConfig);

	const user = peopleData.find((person) => person.fBPersonID === FbPersonID);

	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const [viewModal, setViewModal] = useState(false);
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [visible, setVisible] = useState(false);
	const displayNames = useGetPersonDisplayName(true);

	const [devices, setDevices] = useState([]);
	const [hours, setHours] = useState(INITIAL_HOURS_DATA);
	const [formData, setFormData] = useState(INITIAL_FORM_DATA);
	const [error, setError] = useState(INITIAL_ERROR_DATA);
	const [disabledEditData, setDisabledEditData] = useState(true);
	const oldState = useRef();

	useEffect(() => {
		setError(INITIAL_ERROR_DATA);
		const EDIT_DATA = {
			startTime: dayjs(dataToUpdate.startTime, 'YYYY-MM-DD'),
			endTime: dayjs(dataToUpdate.endTime, 'YYYY-MM-DD'),
			startHour: dayjs(dataToUpdate.startTime + 'Z').local(),
			endHour: dayjs(dataToUpdate.endTime + 'Z').local(),
			description: dataToUpdate.description,
			name: dataToUpdate.name,
			deviceID: dataToUpdate.deviceID,
			assignedToPerson: dataToUpdate.assignedToPerson
		};
		oldState.current = EDIT_DATA;
		form.setFieldsValue(!isEdit ? INITIAL_FORM_DATA : EDIT_DATA);
		setFormData(!isEdit ? INITIAL_FORM_DATA : EDIT_DATA);
	}, [
		form,
		isEdit,
		dataToUpdate.startTime,
		dataToUpdate.endTime,
		dataToUpdate.description,
		dataToUpdate.name,
		dataToUpdate.deviceID,
		dataToUpdate.assignedToPerson,
		dataToUpdate.assignedToTeam,
		dataToUpdate.recurringEndDate,
		dataToUpdate.recurringFrequency,
		dataToUpdate.recurringInterval,
		viewModal,
		visible
	]);

	useEffect(() => {
		setDevices(devicesData.filter((device) => device.typeID === 3));
	}, [devicesData]);

	useLayoutEffect(() => {
		let startTime = dayjs(formData.startTime, 'YYYY-MM-DD');
		let endTime = dayjs(formData.endTime, 'YYYY-MM-DD');
		let startHour = dayjs(hours.startHour);
		let endHour = dayjs(hours.endHour);

		let finalStartTime = startTime.clone();
		finalStartTime.set({
			hour: startHour.hour(),
			minutes: startHour.minute()
		});

		//Set the end date
		let finalEndTime = endTime.clone();
		finalEndTime.set({
			hour: endHour.hour(),
			minutes: endHour.minute()
		});

		if (startTime.isAfter(endTime)) {
			setError({
				error: true,
				message: 'The end date must be greater than or equal to the start date.',
				type: 'date'
			});
		} else if (finalStartTime.isAfter(finalEndTime)) {
			setError({
				error: true,
				message: 'The end hour must be greater than the start hour.',
				type: 'hour'
			});
		}
		return () => {
			setError(INITIAL_ERROR_DATA);
		};
	}, [formData.startTime, formData.endTime, hours.endHour, hours.startHour, isEdit, visible]);

	useEffect(() => {
		//set initial hours
		setHours({
			startHour: !isEdit ? INITIAL_HOURS_DATA.startHour : dayjs(dataToUpdate.startTime + 'Z').local(),
			endHour: !isEdit ? INITIAL_HOURS_DATA.endHour : dayjs(dataToUpdate.endTime + 'Z').local()
		});
	}, [dataToUpdate.startTime, dataToUpdate.endTime, isEdit]);

	const handleSubmit = async (values) => {
		setConfirmLoading(true);
		//Set the start date
		let finalStartTime = values.startTime.clone();
		finalStartTime.set({
			hour: values.startHour.hour(),
			minutes: values.startHour.minute()
		});

		//if the start date is the same of current time, return current time plus 1 minute
		finalStartTime = !isDateInFuture(finalStartTime) ? currentAcceptableDate() : finalStartTime;

		//Set the end date
		let finalEndTime = values.endTime.clone();
		finalEndTime.set({
			hour: values.endHour.hour(),
			minutes: values.endHour.minute()
		});

		const body = {
			name: values.name,
			...(values.description ? { description: values.description } : null),
			bookingTypeID: formData.bookingTypeID,
			deviceID: values.deviceID,
			startTime: finalStartTime,
			endTime: finalEndTime,
			timezone: tz.guess(),
			assignedToPerson: values.assignedToPerson ? values.assignedToPerson : user.personID,
			...(values.assignedToTeam ? { assignedToTeam: values.assignedToTeam } : null)
		};

		// send data to the database depends of the method being called
		let res;
		if (!isEdit) {
			res = await SuperFetch.post('/booking', body, {});
		} else {
			delete body.bookingTypeID;
			res = await SuperFetch.patch(`/booking/${dataToUpdate.bookingID}`, body, {});
		}

		if (res.statusCode === 200) {
			Notification('success', !isEdit ? res.message : 'Updated booking');
			dispatch(bookingActions.loadBookings());
			setConfirmLoading(false);
			!isEdit ? setViewModal(false) : setVisible(false);
			form.resetFields();
		} else {
			setConfirmLoading(false);
			Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
		}
	};

	const handleCancel = () => {
		isEdit ? setVisible(false) : setViewModal(false);
		form.resetFields();
		setError(INITIAL_ERROR_DATA);
		setDisabledEditData(true);
	};

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={isEdit ? 'Update Booking' : 'Create a Booking'}
				condition={true}
				disabledCondition={false}
				trueTitle={''}
				falseTitle={''}
				onClick={() => {
					if (isEdit) {
						setVisible(true);
						setFormData(dataToUpdate);
					} else {
						setViewModal(true);
					}
				}}
				size={isEdit ? 'small' : 'default'}
				type={isEdit ? 'default' : 'primary'}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				title={!isEdit ? 'Add New Booking' : 'Edit Booking'}
				open={!isEdit ? viewModal : visible}
				onCancel={() => handleCancel()}
				width={650}
				destroyOnClose={true}
				footer={[
					<Button key='reset' onClick={() => handleCancel()}>
						Cancel
					</Button>,
					<Button
						loading={confirmLoading}
						form={form}
						key='submit'
						htmlType='submit'
						type='primary'
						disabled={disabledEditData}
					>
						{!isEdit ? 'Add Booking' : 'Edit Booking'}
					</Button>
				]}
			>
				<Form
					form={form}
					id={form}
					onFinish={handleSubmit}
					layout='vertical'
					onValuesChange={(props, values) => {
						const res = _.isEqual(oldState.current, {
							...values,
							description: values.description ? values.description : null
						});
						setDisabledEditData(res);
					}}
				>
					<Row>
						<Col lg={24} md={24} sm={24} xs={24} style={{ padding: 20 }}>
							<Form.Item name='name' label='Booking Name' rules={fieldsValidator.Required}>
								<Input />
							</Form.Item>
							<Row gutter={5}>
								<Col lg={24} md={24} sm={24} xs={24}>
									<Form.Item
										name='assignedToPerson'
										label='Select User'
										rules={fieldsValidator.Required}
									>
										<Select
											showSearch
											placeholder={'Select User'}
											options={peopleData.map((person) => {
												return {
													label: displayNames[person.personID]?.displayName,
													value: person.personID,
													title: displayNames[person.personID]?.title
												};
											})}
											filterOption={(input, option) =>
												String(option?.label ?? '')
													.toLowerCase()
													.includes(String(input).toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Form.Item name='deviceID' label='Device' rules={fieldsValidator.Required}>
								<Select
									showSearch
									placeholder={'Select device'}
									options={devices.map((device) => {
										return {
											label: device.name,
											value: device.uID,
											defaultCategoryID: device.defaultCategoryID
										};
									})}
									disabled={!isEdit ? false : true}
									onChange={(e, props) =>
										setFormData({
											...formData,
											bookingTypeID: constants.ACTIONABLE_SMART_LOCKS.includes(
												props.defaultCategoryID
											)
												? bookingType.LOCKER
												: bookingType.STANDARD
										})
									}
									filterOption={(input, option) =>
										String(option?.label ?? '')
											.toLowerCase()
											.includes(String(input).toLowerCase())
									}
								/>
							</Form.Item>
							<Row lg={24} md={24} sm={24} xs={24} gutter={5}>
								<Col lg={12} md={12} sm={12} xs={24}>
									<Row gutter={5}>
										<Col lg={12} md={12} sm={12} xs={12}>
											<Form.Item
												name='startTime'
												label={<span>Start Time </span>}
												rules={fieldsValidator.Required}
												validateStatus={error.error && error.type === 'date' ? 'error' : null}
											>
												<DatePicker
													format='DD/MM/YYYY'
													allowClear={false}
													disabledDate={(d) => !d || d.isBefore(dayjs().add(-1, 'day'))}
													placeholder={'Date start'}
													onChange={(e) =>
														setFormData({
															...formData,
															startTime: e
														})
													}
												/>
											</Form.Item>
										</Col>

										<Col lg={12} md={12} sm={12} xs={12}>
											<Form.Item
												name={'startHour'}
												label={<span>Start Hour</span>}
												rules={fieldsValidator.Required}
												validateStatus={error.error && error.type === 'hour' ? 'error' : null}
											>
												<TimePicker
													placeholder={'Start hour'}
													format='HH:mm'
													onChange={(e) =>
														setHours({
															...hours,
															startHour: e
														})
													}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={5}>
										<Col lg={12} md={12} sm={12} xs={12}>
											<Form.Item
												name='endTime'
												label={<span>End Time</span>}
												rules={fieldsValidator.Required}
												validateStatus={error.error && error.type === 'date' ? 'error' : null}
											>
												<DatePicker
													format='DD/MM/YYYY'
													allowClear={false}
													placeholder={'Date end'}
													disabledDate={(d) => d.isBefore(formData.startTime)}
													onChange={(e) =>
														setFormData({
															...formData,
															endTime: e
														})
													}
												/>
											</Form.Item>
										</Col>

										<Col lg={12} md={12} sm={12} xs={12}>
											<Form.Item
												rules={fieldsValidator.Required}
												validateStatus={error.error && error.type === 'hour' ? 'error' : null}
												name={'endHour'}
												label={<span>End Hour</span>}
											>
												<TimePicker
													format='HH:mm'
													placeholder={'End hour'}
													onChange={(e) =>
														setHours({
															...hours,
															endHour: e
														})
													}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Col>
							</Row>
							{error.error && (
								<Text text={error.message || ''} color='red'>
									{error.message || ''}
								</Text>
							)}
							<Col lg={24} md={24} sm={24} xs={24} gutter={5}>
								<Form.Item name='description' label='Description' rules={fieldsValidator.Description}>
									<TextArea
										style={{ color: colorTextDescription }}
										allowClear
										autoSize={{ minRows: 2, maxRows: 6 }}
									/>
								</Form.Item>
							</Col>
						</Col>
					</Row>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default AddBookingModal;
